import React from "react";
import CustomerContactCard from "./CustomerContactCard";
import OrderStatusTimeline from "./OrderStatusTimeline";
import OrderItemsList from "./OrderItemsList";

interface User {
  role: string;
}

interface OrderDetailsLeftColumnProps {
  order: any;
  user?: User | null;
  products: any[];
  isRTL?: boolean;
  t: (key: string) => string;
  formatCurrency: (value: number) => string;
  getPaymentMethodText: (method: string) => string;
  paymentMethodIcons: Record<string, React.ReactNode>;
  paymentStatusColors: Record<string, string>;

  // Contact
  showContactOptions: boolean;
  onToggleContact: () => void;
  formatPhoneForWhatsApp: (phone: string) => string;
  getWhatsAppMessage: () => string;

  // Timeline
  getStatusText: (status: string) => string;

  // Items
  isItemsExpanded: boolean;
  onToggleItems: () => void;

  // Actions
  onOpenStatusModal: () => void;
  onOpenPaymentModal: () => void;
}

const OrderDetailsLeftColumn: React.FC<OrderDetailsLeftColumnProps> = ({
  order,
  user,
  products,
  isRTL = false,
  t,
  formatCurrency,
  getPaymentMethodText,
  paymentMethodIcons,
  paymentStatusColors,
  showContactOptions,
  onToggleContact,
  formatPhoneForWhatsApp,
  getWhatsAppMessage,
  getStatusText,
  isItemsExpanded,
  onToggleItems,
  onOpenStatusModal,
  onOpenPaymentModal,
}) => {
  return (
    <div className="lg:col-span-2 space-y-6">
      {/* Order Summary */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div
          className={`flex items-center justify-between mb-6 ${
            isRTL ? "flex-row-reverse" : ""
          }`}
        >
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <span className="text-blue-500">📋</span>
            {t("order.orderSummary")}
          </h2>

          <div className={`flex items-center gap-2 ${isRTL ? "flex-row-reverse" : ""}`}>
            <div
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                paymentStatusColors[order.payment_status]
              }`}
            >
              {t(`paymentStatus.${order.payment_status}`).toUpperCase()}
            </div>

            <div
              className={`flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-lg ${
                isRTL ? "flex-row-reverse" : ""
              }`}
            >
              <span>{paymentMethodIcons[order.payment_method]}</span>
              <span className="text-sm font-medium">
                {getPaymentMethodText(order.payment_method)}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-blue-50 rounded-xl">
            <div className="text-sm text-gray-500 mb-1">{t("order.customer")}</div>
            <div className="font-semibold text-gray-800">
              {order.customer.username}
            </div>
            <div className="text-sm text-gray-600">
              {t("order.id")}: {order.customer.id}
            </div>
          </div>

          <div className="p-4 bg-green-50 rounded-xl">
            <div className="text-sm text-gray-500 mb-1">{t("order.totalAmount")}</div>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(order.total)}
            </div>
          </div>
        </div>

        <CustomerContactCard
          phone={order?.address?.mobile}
          showOptions={showContactOptions}
          onToggle={onToggleContact}
          t={t}
          formatPhoneForWhatsApp={formatPhoneForWhatsApp}
          getWhatsAppMessage={getWhatsAppMessage}
        />

        <OrderStatusTimeline
          currentStatus={order?.status}
          t={t}
          getStatusText={getStatusText}
        />

        {user && ["branch_owner", "super_admin"].includes(user.role) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
            <button
              onClick={onOpenStatusModal}
              className="px-5 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2"
            >
              🔄 {t("order.updateStatus")}
            </button>

            {order.payment_status === "unpaid" && (
              <button
                onClick={onOpenPaymentModal}
                className="px-5 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-medium flex items-center justify-center gap-2"
              >
                💰 {t("order.markAsPaid")}
              </button>
            )}

            {order.payment_status === "paid" && (
              <div className="px-5 py-3 bg-gradient-to-r from-green-50 to-green-100 text-green-700 rounded-xl font-medium flex items-center justify-center gap-2">
                ✅ {t("order.paymentConfirmed")}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Order Items */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <OrderItemsList
          items={order.items}
          products={products}
          isExpanded={isItemsExpanded}
          onToggle={onToggleItems}
          isRTL={isRTL}
          t={t}
          formatCurrency={formatCurrency}
        />
      </div>
    </div>
  );
};

export default OrderDetailsLeftColumn;